import { useId, type InputHTMLAttributes, type Ref } from 'react';
import styles from './Input.module.scss';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  fullWidth?: boolean;
  ref?: Ref<HTMLInputElement>;
}

export function Input({ label, error, fullWidth = true, className, ref, id: externalId, ...props }: InputProps) {
  const generatedId = useId();
  const inputId = externalId ?? generatedId;

  // Direction default — most inputs accept either-direction content (free
  // text, search) so use `dir="auto"` to let the browser detect per first
  // strong character. Protocol-style inputs (email, tel, url, number)
  // render LTR for content correctness regardless of UI direction.
  // Callers can always override by passing an explicit `dir` prop.
  const isProtocolType =
    props.type === 'email' || props.type === 'tel' || props.type === 'url' || props.type === 'number';
  const computedDir = props.dir ?? (isProtocolType ? 'ltr' : 'auto');

  return (
    <div className={`${styles.wrapper} ${fullWidth ? styles.fullWidth : ''} ${className ?? ''}`}>
      {label && <label htmlFor={inputId} className={styles.label}>{label}</label>}
      <input
        id={inputId}
        ref={ref}
        className={`${styles.input} ${error ? styles.inputError : ''}`}
        {...props}
        dir={computedDir}
      />
      {error && <span className={styles.errorText}>{error}</span>}
    </div>
  );
}
